import type { Api } from '@zag-js/tree-view'
import { computed, type ComputedRef, type Ref } from 'vue'
import { AGENT_NAME_FALLBACK, FOLDER_NAME_FALLBACK } from '../Project/constants'
import { useFolderStore } from '../Project/Folders/useFolderStore'
import type { ProjectTreeNode } from './ProjectTree.vue'
import { useProjects } from './useProjects'

/**
 * Places projects into the folder tree and filters the tree based on the
 * given search term.
 */
export const useProjectTreeSearch = (
  searchTerm: Ref<string | undefined>,
): {
  projectTree: ComputedRef<ProjectTreeNode[]>
  expandAncestorsOfMatches: (api: Api, search?: string) => void
} => {
  const folderStore = useFolderStore()
  const projectsStore = useProjects()

  const projectTree = computed<ProjectTreeNode[]>(() => {
    // Make a copy to avoid mutating the original tree
    const treeWithProjects: ProjectTreeNode[] = JSON.parse(JSON.stringify(folderStore.folderTrees))

    // Make a map of all the folders in the tree for efficient updates
    const folderMap = new Map<string, ProjectTreeNode>()
    const mapFolders = (folder: ProjectTreeNode) => {
      folderMap.set(folder.id, folder)
      if (folder.nodeType === 'folder') {
        folder.children.forEach(mapFolders)
      }
    }
    treeWithProjects.forEach(mapFolders)

    // Add projects to the tree
    projectsStore.projects.forEach((p) => {
      if (p.parentFolderId) {
        const parentFolder = folderMap.get(p.parentFolderId)
        if (parentFolder && parentFolder.nodeType === 'folder') {
          parentFolder.children.push({
            ...p,
            nodeType: 'agent',
          })
        }
      } else {
        treeWithProjects.push({
          ...p,
          nodeType: 'agent',
        })
      }
    })

    // If there's a search term, filter the tree
    if (searchTerm.value) {
      const includedNodes = buildNodeInclusionMap(treeWithProjects, searchTerm.value)
      return filterTreeByInclusionMap(treeWithProjects, includedNodes, searchTerm.value)
    }

    return treeWithProjects
  })

  const expandAncestorsOfMatches = (api: Api, search?: string) => {
    api.collapse()
    if (!search) {
      return
    }

    // When searching, expand folders that have matching descendants but don't match themselves
    const foldersToExpand: string[] = []

    // Find folders that need to be expanded - these are ancestors of matches that don't match directly
    const collectExpandableFolders = (node: ProjectTreeNode): boolean => {
      if (node.nodeType === 'agent') {
        return nodeDirectlyMatches(node, search)
      }

      const hasMatchingDescendant = node.children.reduce((acc, child) => {
        return collectExpandableFolders(child) || acc
      }, false)

      if (hasMatchingDescendant) {
        foldersToExpand.push(node.id)
      }

      return nodeDirectlyMatches(node, search) || hasMatchingDescendant
    }

    projectTree.value.forEach((node) => collectExpandableFolders(node))
    if (foldersToExpand.length > 0) {
      api.expand(foldersToExpand)
    }
  }

  return {
    projectTree,
    expandAncestorsOfMatches,
  }
}

/**
 * Helper function to check if a node matches the search term directly
 */
function nodeDirectlyMatches(node: ProjectTreeNode, searchTerm?: string): boolean {
  if (!searchTerm) return true

  const normalizedSearchTerm = searchTerm.toLowerCase()

  const nodeName =
    node.nodeType === 'folder'
      ? node.name || FOLDER_NAME_FALLBACK
      : node.name || AGENT_NAME_FALLBACK

  return nodeName.toLowerCase().includes(normalizedSearchTerm)
}

/**
 * Builds a map of node IDs that should be included in the filtered tree
 */
function buildNodeInclusionMap(nodes: ProjectTreeNode[], searchTerm?: string): Set<string> {
  if (!searchTerm) return new Set() // No filtering needed

  const includedNodes = new Set<string>()

  // First pass: identify direct matches and their descendants (for folders)
  const findMatches = (node: ProjectTreeNode, parentMatches: boolean = false) => {
    const nodeMatches = nodeDirectlyMatches(node, searchTerm)

    // Include if node directly matches or parent matches (for descendants)
    if (nodeMatches || parentMatches) {
      includedNodes.add(node.id)

      // For folders that match, include all descendants
      if (node.nodeType === 'folder') {
        node.children.forEach((child) => findMatches(child, true))
      }
    } else if (node.nodeType === 'folder') {
      // Check children even if this folder doesn't match
      node.children.forEach((child) => findMatches(child, false))
    }
  }

  nodes.forEach((node) => findMatches(node))

  // Second pass: add ancestors of any included node
  const addAncestors = (node: ProjectTreeNode, ancestors: ProjectTreeNode[] = []) => {
    // If this node is included, include all its ancestors
    if (includedNodes.has(node.id)) {
      ancestors.forEach((ancestor) => {
        includedNodes.add(ancestor.id)
      })
    }

    // Recursively process children with updated ancestors list
    if (node.nodeType === 'folder') {
      const newAncestors = [...ancestors, node]
      node.children.forEach((child) => addAncestors(child, newAncestors))
    }
  }

  // Process each top-level node (with empty ancestors list)
  nodes.forEach((node) => addAncestors(node))

  return includedNodes
}

/**
 * Filter a tree based on the inclusion map
 */
function filterTreeByInclusionMap(
  nodes: ProjectTreeNode[],
  includedNodes: Set<string>,
  searchTerm: string | undefined,
): ProjectTreeNode[] {
  if (!searchTerm?.trim()) {
    return nodes
  }

  return nodes
    .filter((node) => includedNodes.has(node.id))
    .map((node) => {
      if (node.nodeType === 'folder') {
        return {
          ...node,
          children: filterTreeByInclusionMap(node.children, includedNodes, searchTerm),
        }
      }
      return node
    })
}
